import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Api } from 'vopay';
import { ApiProvider } from 'shared';
import { api } from '@/services/api.config';

type PartnerSettingsApiProviderProps = {
  /** A view-only partner role: every write below is refused with a message the page shows. */
  readOnly?: boolean;
  children: ReactNode;
};

/**
 * Points the account calls of the shared Settings pages (Branding, Webhooks) at the partner's own routes
 * (`/partner/branding*`, `/partner/webhook-url*`, `/partner/webhooks`, `/partner/webhook/resend`), which
 * take and return the same shapes, so Settings manages the partner's own branding and webhooks with the
 * redesigned pages. Shared never learns which host it is in.
 */
export default function PartnerSettingsApiProvider({ readOnly = false, children }: PartnerSettingsApiProviderProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.PartnerSettingsApiProvider' });

  const partnerApi = useMemo<Api>(() => {
    const refuseWrite = async () => {
      throw new Error(t('viewOnly', { defaultValue: 'A view-only user cannot make this change.' }));
    };

    return {
      ...api,
      fetchAccountBranding: (params, source) => api.fetchPartnerBranding(params, source),
      postAccountBranding: readOnly ? refuseWrite : (params, source) => api.postPartnerBranding(params, source),
      // The partner route authenticates by the session and reads no user field.
      fetchWebhookInfo: (params, source) => api.fetchPartnerWebhook({ Username: params.PortalUser ?? '' }, source),
      // No Type means every event; the partner route has no Limit/Offset or NotificationStatus and ignores them.
      fetchWebhooks: (params, source) => api.fetchPartnerWebhooks({ ...params, Type: params.Type ?? '' }, source),
      updateWebhook: readOnly ? refuseWrite : (params, source) => api.postPartnerUpdateWebhook(params, source),
      // Sending a test or a retrigger delivers to the partner's endpoint, so both count as writes.
      fetchTestWebhook: readOnly ? refuseWrite : (params, source) => api.fetchPartnerTestWebhook(params, source),
      resendWebhook: readOnly ? refuseWrite : (params, source) => api.postPartnerResendWebhook(params, source),
    };
  }, [readOnly, t]);

  return <ApiProvider api={partnerApi}>{children}</ApiProvider>;
}
